'use client';
import { useAtomValue, useAtom } from 'jotai/react';
import { memo, PropsWithChildren, useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import useSWRMutation from 'swr/mutation';
import {
  chatNovelScriptAtom,
  maximumShownOpIndexAtom,
  storyAtom,
  updateSmaximumShownOpIndexAtomEffect,
} from '~/app/(default-layout)/s/[storyId]/_state/chat-render-atoms';
import { removeReadingProgressItem } from '~/app/_hooks/use-local-reading-progress';
import { updateReadingProgress } from '~/app/_repositories/me-service';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { useFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import { useTrackSeriesNextStoryToRead } from '~/common/hooks/use-track-series-next-story-to-read';
import { Box } from '@styled-system/jsx';

type Props = PropsWithChildren<{
  nextStoryId?: string;
  nextStorySeriesIndex?: number;
  nextStoryIsVipOnly?: boolean;
}>;

const updateReadingProgressOnFinish =
  (storyId: string, index: number) => async () => {
    if (index > 0) await updateReadingProgress({ story_id: storyId, index });
    removeReadingProgressItem(storyId);
  };

// Observe whether the elements of OperationItem are shown.
const _ReadEventContainer = ({
  children,
  nextStoryId,
  nextStorySeriesIndex,
  nextStoryIsVipOnly,
}: Props) => {
  const { logEvent } = useFirebaseAnalytics();
  const { session } = useTellerSession();
  const { storeNextStoryToRead, removeStoredSeriesStoryTracking } =
    useTrackSeriesNextStoryToRead();

  const maximumShownOpIndex = useAtomValue(maximumShownOpIndexAtom);
  const story = useAtomValue(storyAtom);
  const chatNovelScript = useAtomValue(chatNovelScriptAtom);

  // subscribe maximumShownOpIndex effect
  useAtom(updateSmaximumShownOpIndexAtomEffect);

  const [sentStoryStartRead, setSentStoryStartRead] = useState(false);
  const beginRef = useRef<HTMLDivElement>(null);
  const beginIntersection = useIntersection(beginRef, {
    root: null,
    threshold: 0,
  });

  // update reading progress on finish
  const { trigger: updateReadingProgressOnFinishTrigger } = useSWRMutation(
    story && chatNovelScript
      ? [story.id, chatNovelScript.totalOpsCount - 1]
      : undefined,
    ([storyId, index]) => updateReadingProgressOnFinish(storyId, index)
  );

  // send story_start_read event
  if (
    !sentStoryStartRead &&
    (beginIntersection?.isIntersecting === true || maximumShownOpIndex > 0) &&
    // Check if the session exists
    session?.uid &&
    story
  ) {
    setSentStoryStartRead(true);

    logEvent('story_start_read', {
      story_id: story.id,
      series_id: story.series?.id,
      like: story.likeCount,
      tags: story.series?.tags ?? [],
      is_official: story.series?.isOfficial ?? false,
    });
  }

  const [sentStoryFinishRead, setSentStoryFinishRead] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const endIntersection = useIntersection(endRef, {
    root: null,
    threshold: 0,
  });

  // send story_finish_read event
  if (
    !sentStoryFinishRead &&
    endIntersection?.isIntersecting === true &&
    story &&
    chatNovelScript &&
    // 最後のopが表示されたとき
    maximumShownOpIndex + 1 >= chatNovelScript.totalOpsCount &&
    // Check if the session exists
    session?.uid
  ) {
    setSentStoryFinishRead(true);

    logEvent('story_finish_read', {
      story_id: story.id,
      series_id: story.series?.id,
      tags: story.series?.tags ?? [],
      is_official: story.series?.isOfficial ?? false,
    });

    updateReadingProgressOnFinishTrigger();

    if (story.series?.id && nextStoryId && nextStorySeriesIndex) {
      storeNextStoryToRead(
        story.series.id || '',
        nextStoryId,
        nextStorySeriesIndex,
        nextStoryIsVipOnly ?? false
      );
    } else {
      // If no next story, remove stored series story tracking count
      // So user can start from the beginning again
      removeStoredSeriesStoryTracking();
    }
  }

  return (
    <>
      <Box ref={beginRef} />
      {children}
      <Box ref={endRef} />
    </>
  );
};

export const ReadEventContainer = memo(_ReadEventContainer);
